import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../nav_bar/Navbar";
import Footer from "../../footer/Footer";
import { fetchReadingById } from "./readingService";
import "./ReadingDetailPage.css";

const difficultyColor = (d) => {
  const map = { beginner: "#4caf50", intermediate: "#ff9800", advanced: "#e91e63" };
  return map[d?.toLowerCase()] || "#999";
};

const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([-\w]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
};

const TrueFalseSection = ({ statements }) => {
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState({});

  const select = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setChecked((prev) => ({ ...prev, [id]: true }));
  };

  return (
    <div className="rdp-tf-list">
      {statements.map((s) => {
        const userAnswer = answers[s.id];
        const isChecked = checked[s.id];
        const isCorrect = userAnswer === s.correctAnswer;
        return (
          <div key={s.id} className="rdp-tf-item">
            <span className="rdp-tf-statement">{s.statement}</span>
            <div className="rdp-tf-toggle">
              <button
                className={`rdp-tf-btn ${isChecked && userAnswer === true ? "selected-true" : ""}`}
                onClick={() => select(s.id, true)}
              >
                True
              </button>
              <button
                className={`rdp-tf-btn ${isChecked && userAnswer === false ? "selected-false" : ""}`}
                onClick={() => select(s.id, false)}
              >
                False
              </button>
            </div>
            {isChecked && (
              <span className={`rdp-tf-result ${isCorrect ? "correct" : "wrong"}`}>
                {isCorrect ? "✓ Correct" : `✗ ${s.correctAnswer ? "True" : "False"}`}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CopyButton = ({ questions }) => {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    const text = questions.map((q, i) => `${i + 1}. ${q.question}`).join("\n");
    try { await navigator.clipboard.writeText(text); }
    catch { const el = document.createElement("textarea"); el.value = text; document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el); }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handle} style={{ background: copied ? "#e8f5e9" : "#f0fafa", border: `1px solid ${copied ? "#a5d6a7" : "#b2dfdb"}`, color: copied ? "#2e7d32" : "#019095", borderRadius: 999, padding: "4px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}>
      {copied ? "✓ Copied!" : "Copy"}
    </button>
  );
};

const ReadingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSession = location.state?.from === "session";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await fetchReadingById(id);
      if (res.status === 200 && res.data?.status) {
        setData(res.data.data);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) return (<><Navbar /><div className="rdp-page"><p className="rdp-loading">Loading...</p></div><Footer /></>);
  if (!data) return (<><Navbar /><div className="rdp-page"><p className="rdp-loading">Passage not found.</p><button className="rdp-back" onClick={() => navigate("/study/reading")}>← Back</button></div><Footer /></>);

  return (
    <>
      <Navbar />
      <div className="rdp-page">
        <button className="rdp-back" onClick={() => fromSession ? navigate("/") : navigate("/study/reading")}>
          {fromSession ? "← Back" : "← Back to Reading"}
        </button>

        <div className="rdp-hero">
          <div className="rdp-hero-top">
            <h1 className="rdp-title">{data.title}</h1>
            <span className="rdp-difficulty" style={{ background: difficultyColor(data.difficulty) }}>
              {data.difficulty}
            </span>
          </div>
          {data.category && (
            <div className="rdp-tags">
              <span className="rdp-tag">{data.category}</span>
            </div>
          )}
        </div>

        {/* Discussion Questions */}
        {data.discussionQuestions?.length > 0 && (
          <div className="rdp-card rdp-card-discussion">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <h3 className="rdp-card-title" style={{ margin: 0 }}>💬 Discussion Questions</h3>
              <CopyButton questions={data.discussionQuestions} />
            </div>
            <ul className="rdp-question-list">
              {data.discussionQuestions.map((q) => (
                <li key={q.id} className="rdp-question-item">{q.question}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Reading Content */}
        <div className="rdp-card rdp-passage-card">
          <h3 className="rdp-card-title">📖 Reading Passage</h3>
          <div
            className="rdp-content"
            dangerouslySetInnerHTML={{ __html: data.content }}
          />
        </div>

        {/* Warm-up Questions */}
        {data.warmupQuestions?.length > 0 && (
          <div className="rdp-card rdp-card-warmup">
            <h3 className="rdp-card-title">🔥 Warm-up Questions</h3>
            <ul className="rdp-question-list">
              {data.warmupQuestions.map((q) => (
                <li key={q.id} className="rdp-question-item">{q.question}</li>
              ))}
            </ul>
          </div>
        )}

        {/* True / False */}
        {data.trueFalseStatements?.length > 0 && (
          <div className="rdp-card rdp-card-truefalse">
            <h3 className="rdp-card-title">✅ True or False?</h3>
            <TrueFalseSection statements={data.trueFalseStatements} />
          </div>
        )}

        {/* Vocabulary — clickable chips */}
        {data.vocabularies?.length > 0 && (
          <div className="rdp-card rdp-card-vocab">
            <h3 className="rdp-card-title">📚 Vocabulary</h3>
            <div className="rdp-chip-list">
              {data.vocabularies.map((v) => (
                <div key={v.id} className="rdp-chip" onClick={() => navigate(`/study/vocabulary/${v.id}`, { state: { from: "reading", readingId: id } })}>
                  <span className="rdp-chip-label">{v.word}</span>
                  {v.partOfSpeech && <span className="rdp-chip-sub">{v.partOfSpeech}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Phrases — clickable chips */}
        {data.phrases?.length > 0 && (
          <div className="rdp-card rdp-card-phrases">
            <h3 className="rdp-card-title">💡 Used Phrases</h3>
            <div className="rdp-chip-list">
              {data.phrases.map((p) => (
                <div key={p.id} className="rdp-chip" onClick={() => navigate(`/study/phrases/${p.id}`, { state: { from: "reading", readingId: id } })}>
                  <span className="rdp-chip-label">{p.title}</span>
                  {p.meaning && <span className="rdp-chip-sub">{p.meaning}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* YouTube / Vimeo Video */}
        {getEmbedUrl(data.youtubeUrl) && (
          <div className="rdp-card rdp-card-video">
            <h3 className="rdp-card-title">🎬 Video</h3>
            <div className="rdp-video-wrapper">
              <iframe
                src={getEmbedUrl(data.youtubeUrl)}
                title={data.title}
                allowFullScreen
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
              />
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default ReadingDetailPage;
